import React, { FunctionComponent, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { filter, append, equals } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ISurveyQuestion } from '../../../../common/models/survey/ISurveyQuestion';
import { setSelectedQuestionsRoutine } from '../../routines';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import SearchInput from '../../../../components/SearchInput';
import { IQuestionWithOwner } from '../../../../common/models/question/IQuestionWithOwner';
import Table from '../../../../components/Table';
import { getColumns, filterData } from './utils';
import { getUpdatedData, getDefaultSelected, getSelected } from '../SurveyBuilder/utils';
import { IAttribute } from '../../../../common/models/attribute/IAttribute';
import AttributesFilter from '../../components/AttributesFilter';
import RegularButton from '../../../../components/RegularButton';
import AttributesCounter from '../AttributesCounter';
import styles from './styles.module.scss';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';
import { QuestionSection } from '../../../../common/enums/QuestionSection';

interface IProps {
  selectedQuestions: ISurveyQuestion[];
  questions: IQuestionWithOwner[];
  attributes: IAttribute[];
  onClose: IBindingAction;
  setSelectedQuestions: IBindingCallback1<ISurveyQuestion[]>;
}

const SoftSkillsPage: FunctionComponent<IProps> = ({
  selectedQuestions,
  questions,
  attributes,
  onClose,
  setSelectedQuestions
}) => {
  const defaultSelectedIds = getDefaultSelected(true, questions, selectedQuestions);

  const [selectedIds, setSelectedIds] = useState(defaultSelectedIds);
  const [search, setSearch] = useState('');
  const [data, setData] = useState(questions);
  const [columns, setColumns] = useState(getColumns(search));
  const [subAttributeIds, setSubAttributeIds] = useState([] as string[]);

  const shownSelected = getSelected(data, selectedIds);
  const allSelected = getSelected(questions, selectedIds);

  useEffect(() => {
    const updatedData = filterData(questions, search, subAttributeIds);

    setData(updatedData);
    setColumns(getColumns(search));
  }, [search, subAttributeIds]);

  const onRowSelected = (row: IQuestionWithOwner & { disabled: boolean }, isSelected: boolean) => {
    setSelectedIds(state => (isSelected
      ? append(row.id, state)
      : filter(id => id !== row.id, state)));
  };

  const onApply = () => {
    setSelectedQuestions(getUpdatedData(true, allSelected, selectedQuestions));
    onClose();
  };

  return (
    <div className="sb-question-page">
      <header className="sb-header">
        <FormattedMessage id="ratingScale.bankTitle" defaultMessage="Soft skills question bank" />
      </header>
      <main className="sb-question-main">
        <div className="sb-table-title">
          <FormattedMessage
            id="ratingScale.bankDescription"
            defaultMessage="Select soft skills questions to add or remove"
            values={{ value: allSelected.length }}
          />
          <div className="d-flex align-items-center">
            <SearchInput
              value={search}
              onChange={value => setSearch(value)}
              defaultPlaceholder="Filter by question text"
              intlId="surveyBuilder.searchPlaceholder"
            />
            <AttributesFilter attributes={attributes} onChange={setSubAttributeIds} />
          </div>
        </div>
        <Table
          className={styles.questionTable}
          config={{
            columns,
            data: { items: data, total: data.length },
            loading: false,
            rowSelection: 'multiple',
            selectionType: 'combine',
            hideCheckboxHeader: true,
            onRowSelected,
            defaultSelected: shownSelected,
            defaultSorted: [{ id: 'subAttribute', desc: false }],
            autoHeight: true,
            noDataPlaceholder: <NoDataPlaceholder variant="questionBank" />,
            noDataPlaceholderHeight: 400
          }}
        />
      </main>
      <footer className="sb-footer">
        <AttributesCounter selectedQuestions={allSelected} />
        <div className="sb-footer-bttns">
          <RegularButton
            variant="tertiary"
            onClick={onClose}
            textIntlId="cancel"
            defaultMessage="Cancel"
          />
          <RegularButton
            textIntlId="surveyBuilder.apply"
            defaultMessage="Apply selection"
            onClick={onApply}
            disabled={equals(defaultSelectedIds, selectedIds)}
          />
        </div>
      </footer>
    </div>
  );
};

const mapStateToProps = ({
  surveyBuilder: { selectedQuestions, questions, attributes }
}: IAppState) => ({ selectedQuestions, questions: questions[QuestionSection.SoftSkills], attributes });

const mapDispatchToProps = {
  setSelectedQuestions: setSelectedQuestionsRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(SoftSkillsPage);
